/* Hauser MX — núcleo de movimiento (Fase 8).
 *
 * ÚNICO lugar que decide qué efectos corren y en qué calidad. Ninguna sección
 * detecta capacidades por su cuenta: todas preguntan aquí.
 *
 * ── Por qué existe ────────────────────────────────────────────────────────
 * El sitio es una máquina de captación de citas para una inmobiliaria de
 * Morelos: la mayoría de las visitas llegan por celular y por datos móviles.
 * La regla que gobierna toda la fase 8 es que NINGÚN efecto puede retrasar el
 * primer render ni bloquear la interacción. Por eso lo pesado (Three.js,
 * Matter.js) no se carga nunca por adelantado: se pide bajo demanda, después
 * del LCP, y solo si el bloque que lo necesita está por entrar en pantalla.
 *
 * ── Los tres niveles ──────────────────────────────────────────────────────
 *   full  → WebGL2 + suficiente CPU. Escenas 3D, shaders, física, cursor.
 *   lite  → WebGL2 pero equipo modesto. LOS MISMOS efectos a menor resolución
 *           y con menos elementos simultáneos. NO es "apagado".
 *   flat  → sin WebGL, o el visitante pidió menos movimiento, o va en modo
 *           ahorro de datos. Cae al CSS que ya existía desde la fase 4.
 *
 * `lite` NO es sinónimo de móvil. Un celular reciente entra en `full` y ve la
 * versión completa; lo que cambia en móvil es el AJUSTE (menos densidad, menos
 * dpr, recorridos de scroll más cortos), no la existencia del efecto. Esa
 * distinción es deliberada: el encargo pedía móvil resuelto, no desactivado.
 *
 * ── Scroll en Horizon (trampa que cuesta cara) ────────────────────────────
 * Horizon usa DOS contenedores de scroll según el ancho (assets/base.css:35 y
 * assets/scroll-container.js):
 *   ≥ 990px → `html` y `body` van en overflow:hidden y el que scrollea es
 *             `.page-wrapper`. `window.scrollY` se queda clavado en 0.
 *   < 990px → scrollea el documento normal (para que la barra del navegador
 *             móvil pueda esconderse).
 * Cualquier ScrollTrigger creado sin decir `scroller` funciona en móvil y NO
 * dispara nada en escritorio. Por eso todo se registra con `Hauser.media()`,
 * que crea el efecto dos veces —una por contenedor— y lo reconstruye solo al
 * cruzar el umbral, vía gsap.matchMedia.
 *
 * NOTA — por qué no hay scroll suave (Lenis): se evaluó y se descartó. Lenis
 * necesita un `wrapper` con un único hijo de contenido, y `.page-wrapper`
 * tiene cuatro (header-group, script, main, footer) además de reglas de
 * Horizon colgadas de él (scroll-lock de los drawers, margen del drawer
 * lateral, restauración manual de scroll). Envolverlos rompería el header
 * pegajoso y los drawers a cambio del efecto más cosmético de la lista.
 * La coreografía la da ScrollTrigger, que no necesita secuestrar el scroll.
 */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;

  /* ── Sondas de capacidad ───────────────────────────────────────────── */

  var params = new URLSearchParams(location.search);

  /* Forzar un nivel desde la URL (?hauser_tier=lite). Es el único modo de
     auditar los tres caminos en un mismo equipo; sin esto, `flat` solo se
     puede ver apagando WebGL en el navegador. */
  var tierForzado = params.get('hauser_tier');

  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mqCoarse = window.matchMedia('(pointer: coarse)');
  var mqScroller = window.matchMedia('(min-width: 990px)');

  function soportaWebGL2() {
    try {
      var c = doc.createElement('canvas');
      return !!(window.WebGL2RenderingContext && c.getContext('webgl2'));
    } catch (e) {
      return false;
    }
  }

  function ahorroDeDatos() {
    var c = navigator.connection;
    if (!c) return false;
    if (c.saveData) return true;
    /* 2g/slow-2g: aquí un shader no es el problema, lo es cargar la foto. */
    return c.effectiveType === '2g' || c.effectiveType === 'slow-2g';
  }

  function calcularNivel() {
    if (tierForzado === 'full' || tierForzado === 'lite' || tierForzado === 'flat') {
      return tierForzado;
    }
    if (mqReduce.matches) return 'flat';
    if (ahorroDeDatos()) return 'flat';
    if (!soportaWebGL2()) return 'flat';

    var nucleos = navigator.hardwareConcurrency || 2;
    var memoria = navigator.deviceMemory || 4;
    if (nucleos >= 4 && memoria >= 4) return 'full';
    return 'lite';
  }

  var tier = calcularNivel();
  var reduced = mqReduce.matches;

  /* ── El objeto público ─────────────────────────────────────────────── */

  var Hauser = {
    tier: tier,
    reduced: reduced,
    activo: tier !== 'flat',

    /* `mobile` es de AJUSTE, no de capacidad: un celular moderno está en
       `full` y ve todo, solo que con menos densidad y menos dpr. */
    get mobile() {
      return mqCoarse.matches || window.innerWidth < 750;
    },

    /* Techo de densidad de píxeles. Un dpr de 3 en un shader a pantalla
       completa multiplica por 9 los píxeles a sombrear; a partir de ~1.75 la
       diferencia ya no se ve en una foto pero sí se siente en el ventilador. */
    get dpr() {
      var d = window.devicePixelRatio || 1;
      var techo = tier === 'full' ? (mqCoarse.matches ? 2 : 1.75) : 1.25;
      return Math.min(d, techo);
    },

    /* El contenedor que realmente scrollea AHORA MISMO. */
    get scroller() {
      if (mqScroller.matches) {
        return doc.querySelector('.page-wrapper') || doc.scrollingElement || root;
      }
      return doc.scrollingElement || root;
    },

    mqScroller: mqScroller,
    mqCoarse: mqCoarse
  };

  window.Hauser = Hauser;

  /* Marcar el <html> para que el CSS pueda responder sin esperar a ningún JS
     y sin parpadeo: se hace de inmediato, antes de pintar. */
  root.setAttribute('data-hauser-tier', tier);
  if (mqCoarse.matches) root.setAttribute('data-hauser-tactil', '');

  /* ── Carga bajo demanda de lo pesado ───────────────────────────────── */

  var cache = {};

  /* Las rutas reales las inyecta layout/theme.liquid en window.HauserAssets,
     porque solo Liquid conoce la URL con hash del CDN de Shopify. */
  function assetUrl(nombre) {
    return (window.HauserAssets && window.HauserAssets[nombre]) || null;
  }

  /* Three.js. El build de r180 viene partido en dos archivos ESM
     (`three.webgpu.min.js` importa `./three.core.min.js` como hermano); los
     dos están subidos como assets del tema y el CDN de Shopify los sirve en
     la misma carpeta, así que el import relativo resuelve solo. Verificado
     contra el CDN real, no supuesto.

     Casi un mega entre los dos: por eso esto NUNCA se llama al cargar la
     página, solo cuando una escena 3D está a punto de entrar en pantalla. */
  Hauser.cargarThree = function () {
    if (cache.three) return cache.three;
    var url = assetUrl('three');
    if (!url || tier === 'flat') {
      cache.three = Promise.reject(new Error('three no disponible en este nivel'));
      /* Evita un unhandledrejection en consola cuando nadie lo espera. */
      cache.three.catch(function () {});
      return cache.three;
    }
    cache.three = import(/* webpackIgnore: true */ url);
    return cache.three;
  };

  /* NOTA — Matter.js se retiró del tema. El plan preveía física real para la
     pila de testimonios y se llegó a vendorizar la librería (83 KB). Al
     construirlo quedó claro que lo que hace falta ahí no es gravedad ni
     colisiones, sino LANZAR una tarjeta y que siga corriendo hasta encajar en
     la siguiente — que es exactamente lo que hacen Draggable + InertiaPlugin,
     ya cargados para los similares de la ficha. Usar Matter habría sumado
     83 KB en todas las páginas para conseguir menos. Si alguna vez hiciera
     falta física de verdad (objetos que chocan entre sí), el patrón de carga
     bajo demanda es el mismo que el de `cargarThree`. */

  /* ── Utilidades compartidas ────────────────────────────────────────── */

  /* Un solo IntersectionObserver reutilizable en vez de uno por sección.
     `once` es lo normal: casi todo aquí es una entrada, no un ciclo. */
  Hauser.enVista = function (el, cb, opciones) {
    opciones = opciones || {};
    if (!('IntersectionObserver' in window)) {
      cb(true);
      return function () {};
    }
    var io = new IntersectionObserver(
      function (entradas) {
        entradas.forEach(function (e) {
          if (e.isIntersecting) {
            cb(true, e);
            if (opciones.once !== false) io.disconnect();
          } else if (opciones.once === false) {
            cb(false, e);
          }
        });
      },
      {
        rootMargin: opciones.rootMargin || '200px 0px',
        threshold: opciones.threshold || 0,
        /* El root tiene que ser el contenedor que scrollea, o en escritorio
           el observer mide contra un viewport que nunca se mueve. */
        root: mqScroller.matches ? doc.querySelector('.page-wrapper') : null
      }
    );
    io.observe(el);
    return function () {
      io.disconnect();
    };
  };

  /* Parte un texto en palabras envueltas para poder animarlas por separado.
     Cada palabra queda como `<span class="hauser-palabra"><span
     class="hauser-palabra__i">…</span></span>`: el contenedor recorta y el
     interior se desplaza, que es lo que da el efecto de "sube desde abajo"
     sin necesidad de ninguna máscara.

     Tres cosas que importan y que un split ingenuo rompe:
     · Se opera sobre nodos de TEXTO, no sobre innerHTML, así que un titular
       con <em> o <br> no pierde su marcado ni se duplica.
     · El texto original se guarda en `aria-label` y el resultado se marca
       `aria-hidden`, porque un lector de pantalla leería 9 spans como 9
       fragmentos sueltos.
     · Los espacios se conservan como nodos aparte; envolverlos también hacía
       que `text-wrap: pretty` no pudiera romper línea. */
  Hauser.partirTexto = function (el) {
    if (!el || el.dataset.hauserPartido) return [];
    var textoPlano = el.textContent.replace(/\s+/g, ' ').trim();

    var piezas = [];
    var nodos = [];
    var caminante = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var n;
    while ((n = caminante.nextNode())) {
      if (n.nodeValue.trim()) nodos.push(n);
    }

    nodos.forEach(function (nodo) {
      var frag = doc.createDocumentFragment();
      var partes = nodo.nodeValue.split(/(\s+)/);
      partes.forEach(function (parte) {
        if (!parte) return;
        if (/^\s+$/.test(parte)) {
          frag.appendChild(doc.createTextNode(' '));
          return;
        }
        var fuera = doc.createElement('span');
        fuera.className = 'hauser-palabra';
        var dentro = doc.createElement('span');
        dentro.className = 'hauser-palabra__i';
        dentro.textContent = parte;
        fuera.appendChild(dentro);
        frag.appendChild(fuera);
        piezas.push(dentro);
      });
      nodo.parentNode.replaceChild(frag, nodo);
    });

    el.setAttribute('aria-label', textoPlano);
    el.dataset.hauserPartido = '1';
    /* Los hijos se ocultan a la accesibilidad, pero el elemento conserva su
       rol (h1/h2) y su aria-label, así que sigue contando como encabezado. */
    Array.prototype.forEach.call(el.children, function (c) {
      c.setAttribute('aria-hidden', 'true');
    });
    return piezas;
  };

  /* Puntero global suavizado (0..1 en cada eje, y -1..1 centrado). Lo usan el
     cursor magnético, la llave 3D y los reflejos. Se calcula UNA vez por
     cuadro para todos los consumidores en vez de que cada efecto ponga su
     propio listener de mousemove. */
  var puntero = { x: 0.5, y: 0.5, cx: 0, cy: 0, sx: 0.5, sy: 0.5, scx: 0, scy: 0, activo: false };
  Hauser.puntero = puntero;

  if (!mqCoarse.matches) {
    window.addEventListener(
      'pointermove',
      function (e) {
        puntero.x = e.clientX / window.innerWidth;
        puntero.y = e.clientY / window.innerHeight;
        puntero.cx = puntero.x * 2 - 1;
        puntero.cy = puntero.y * 2 - 1;
        puntero.activo = true;
      },
      { passive: true }
    );
  }

  /* Velocidad de scroll normalizada (-1..1). El header y varias entradas la
     usan para reaccionar a la INTENSIDAD del gesto, no solo a la posición:
     es la diferencia entre una página que se mueve y una que responde. */
  var scroll = { y: 0, delta: 0, velocidad: 0, direccion: 1 };
  Hauser.scroll = scroll;

  /* ── Bucle único ───────────────────────────────────────────────────── */

  var tareas = [];

  Hauser.porCuadro = function (fn) {
    tareas.push(fn);
    return function () {
      var i = tareas.indexOf(fn);
      if (i > -1) tareas.splice(i, 1);
    };
  };

  function tick(tiempo, delta) {
    /* Suavizado exponencial independiente de los FPS: `1 - exp(-k*dt)` da el
       mismo resultado a 60 y a 120 Hz. Con un factor fijo (0.1 por cuadro) un
       monitor de 120 Hz duplicaba la velocidad de todos los seguimientos. */
    var k = 1 - Math.exp(-12 * (delta / 1000));
    puntero.sx += (puntero.x - puntero.sx) * k;
    puntero.sy += (puntero.y - puntero.sy) * k;
    puntero.scx += (puntero.cx - puntero.scx) * k;
    puntero.scy += (puntero.cy - puntero.scy) * k;

    var y = Hauser.scroller.scrollTop || 0;
    scroll.delta = y - scroll.y;
    scroll.y = y;
    if (scroll.delta !== 0) scroll.direccion = scroll.delta > 0 ? 1 : -1;
    /* Se satura a ±1 con un divisor generoso: 60px de desplazamiento en un
       cuadro ya es un gesto rápido. */
    var v = Math.max(-1, Math.min(1, scroll.delta / 60));
    scroll.velocidad += (v - scroll.velocidad) * k;

    for (var i = 0; i < tareas.length; i++) {
      try {
        tareas[i](tiempo, delta);
      } catch (err) {
        /* Un efecto que truena no puede tumbar a los demás ni al bucle. */
        if (window.console) console.warn('[Hauser] efecto con error, se retira:', err);
        tareas.splice(i, 1);
        i--;
      }
    }
  }

  /* ── Arranque de GSAP ──────────────────────────────────────────────── */

  var listos = [];
  var gsapListo = false;

  Hauser.listo = function (fn) {
    if (gsapListo) fn(window.gsap);
    else listos.push(fn);
  };

  /* `Hauser.media(fn)` — registra un efecto de scroll UNA vez por cada
     contenedor de scroll posible. gsap.matchMedia destruye y reconstruye solo
     al cruzar los 990px, así que no hay que escuchar `resize` a mano ni
     quedarse con triggers apuntando a un scroller que ya no scrollea.
     `fn` recibe { scroller, escritorio, mobile } y todo lo que cree dentro se
     limpia solo. */
  Hauser.media = function (fn) {
    Hauser.listo(function (gsap) {
      if (!gsap.matchMedia) return;
      var mm = gsap.matchMedia();
      mm.add('(min-width: 990px)', function () {
        return fn({
          scroller: doc.querySelector('.page-wrapper') || undefined,
          escritorio: true,
          mobile: false
        });
      });
      mm.add('(max-width: 989px)', function () {
        return fn({
          scroller: undefined, /* undefined = el scroller por defecto (window) */
          escritorio: false,
          mobile: window.innerWidth < 750
        });
      });
    });
  };

  function arrancar() {
    var gsap = window.gsap;
    if (!gsap) {
      /* Sin GSAP el sitio sigue funcionando: el CSS de la fase 4 ya cubre
         todas las entradas. Se degrada, no se rompe. */
      root.setAttribute('data-hauser-tier', 'flat');
      Hauser.tier = 'flat';
      Hauser.activo = false;
      return;
    }

    if (window.ScrollTrigger) {
      gsap.registerPlugin(window.ScrollTrigger);
      /* En escritorio el scroll vive dentro de .page-wrapper, que además
         cambia de altura cuando entran imágenes lazy: sin este refresco los
         triggers quedan calculados contra una altura vieja. */
      window.addEventListener('load', function () {
        window.ScrollTrigger.refresh();
      });
    }
    if (window.Flip) gsap.registerPlugin(window.Flip);
    if (window.Draggable) gsap.registerPlugin(window.Draggable);
    if (window.InertiaPlugin) gsap.registerPlugin(window.InertiaPlugin);

    /* Un solo bucle para todo el sitio, el de GSAP, en vez de N rAF sueltos. */
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(500, 33);

    gsapListo = true;
    listos.forEach(function (fn) {
      try {
        fn(gsap);
      } catch (e) {
        if (window.console) console.warn('[Hauser] init con error:', e);
      }
    });
    listos.length = 0;

    root.setAttribute('data-hauser-listo', '');
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', arrancar, { once: true });
  } else {
    arrancar();
  }

  /* Si el visitante cambia su preferencia de movimiento en caliente, se
     respeta al instante en vez de esperar a una recarga. */
  mqReduce.addEventListener('change', function (e) {
    if (e.matches) {
      Hauser.tier = 'flat';
      Hauser.activo = false;
      root.setAttribute('data-hauser-tier', 'flat');
      if (window.ScrollTrigger) window.ScrollTrigger.getAll().forEach(function (t) { t.kill(); });
    }
  });
})();
